import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { PermissionArea } from '@pbt/pbt-ui-components'

import TaskStatusSelect from '~/components/dashboard/tasks-dashboard/TaskStatusSelect'
import { TaskStatusSelectV2 } from '~/components/dashboard/tasks-dashboard/TaskStatusSelectV2'
import DialogNames from '~/constants/DialogNames'
import FeatureToggle from '~/constants/featureToggle'
import { fetchTask } from '~/store/actions/tasks'
import { getCRUDByArea } from '~/store/reducers/auth'
import { getFeatureToggle } from '~/store/reducers/constants'
import { getTask } from '~/store/reducers/tasks'
import type { Task, TaskTimelineItem } from '~/types'
import useDialog from '~/utils/useDialog'

import TimelineCardActions from '../TimelineCardActions'
import TimelineCardContent from '../TimelineCardContent'
import { useTaskCardProps } from './hooks/useTaskCardProps'
import { useTaskIsEditable } from './hooks/useTaskIsEditable'
import MultipleTasks from './MultipleTasks'
import OneTask from './OneTask'

import styles from './taskCardContent.module.scss'

export interface TaskCardContentProps {
  additionalContent?: React.ReactNode
  clientId: string
  item: TaskTimelineItem
  patientId: string
}

export const TaskCardContent = ({
  item,
  clientId,
  patientId,
  additionalContent,
}: TaskCardContentProps) => {
  const dispatch = useDispatch()
  const hasOneTask = item.entries.length === 1
  const taskId = item.entries[0]

  const firstTask = useSelector(getTask(taskId))
  const permissions = useSelector(getCRUDByArea(PermissionArea.TASK))
  const isTaskImprovementsPhase1Enabled = useSelector(
    getFeatureToggle(FeatureToggle.TASKS_IMPROVEMENTS_PHASE_1),
  )

  const [openTaskDialog] = useDialog(DialogNames.TASK)
  const taskIsEditable = useTaskIsEditable(firstTask)

  const onEdit = (task: Task) => {
    dispatch(fetchTask(task.id))
    openTaskDialog({ clientId, patientId, taskId: task.id })
  }

  const TaskStatusSelectComponent = isTaskImprovementsPhase1Enabled
    ? TaskStatusSelectV2
    : TaskStatusSelect

  return (
    <>
      <TimelineCardContent noTypography>
        {hasOneTask ? (
          <OneTask showHistory={false} showState={false} taskId={taskId} />
        ) : (
          <MultipleTasks
            enableBusinessShareIcon
            item={item}
            onEdit={permissions.update ? onEdit : undefined}
          />
        )}
        {additionalContent}
      </TimelineCardContent>
      {hasOneTask && (
        <TimelineCardActions
          customStateControl={
            <TaskStatusSelectComponent
              className={styles.taskStateLabel}
              disabled={!permissions.update}
              fullWidth={false}
              task={firstTask}
            />
          }
          item={firstTask}
        />
      )}
    </>
  )
}

export { useTaskCardProps }
